// First-party visitor analytics: batches events to /public/analytics/collect. Never blocks the UI.
import { API_BASE, getToken } from "@/lib/api";

const VID = "rp_vid", CONSENT = "rp_consent", PLAN = "rp_checkout_plan";
const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"];
const UTM = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

const readCookie = (k) => document.cookie.split("; ").find((c) => c.startsWith(`${k}=`))?.split("=")[1];
export const visitorId = () => {
  let v = localStorage.getItem(VID) || readCookie(VID);
  if (!v) v = uuid();
  localStorage.setItem(VID, v);
  document.cookie = `${VID}=${v}; max-age=31536000; path=/; SameSite=Lax`;
  return v;
};

export const consent = () => localStorage.getItem(CONSENT) !== "declined"; // track by default, opt-out
export const consentChoice = () => localStorage.getItem(CONSENT);
export const setConsent = (ok) => localStorage.setItem(CONSENT, ok ? "accepted" : "declined");

let queue = [];
let first = null; // attribution context for the first page of the visit only
const gpc = () => navigator.globalPrivacyControl === true;

const baseContext = () => ({
  consent: { analytics: consent() }, gpc: gpc(),
  screen: `${window.screen.width}x${window.screen.height}`, viewport: `${window.innerWidth}x${window.innerHeight}`,
  language: navigator.language, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, platform: "web",
});

const captureFirst = () => {
  if (sessionStorage.getItem("rp_visit")) return;
  sessionStorage.setItem("rp_visit", "1");
  const q = new URLSearchParams(window.location.search);
  first = { referrer: document.referrer || null };
  [...UTM, ...CLICK_IDS].forEach((k) => { if (q.get(k)) first[k] = q.get(k); });
  if (q.get("ref")) queue.push(ev("referral_link_click", { code: q.get("ref").slice(0, 40) }));
};

const ev = (name, props = {}) => ({ id: uuid(), name, ts: new Date().toISOString(), path: window.location.pathname, url: window.location.origin + window.location.pathname, referrer: document.referrer || null, title: document.title, props });

export const track = (name, props) => { try { queue.push(ev(name, props)); } catch { /* never throw */ } };

const flush = (beacon) => {
  if (!queue.length || !API_BASE) return;
  const body = { visitor_id: visitorId(), events: queue.splice(0, 50), context: { ...baseContext(), ...(first || {}) } };
  first = null;
  const url = `${API_BASE}/public/analytics/collect`;
  try {
    const t = getToken();
    if (beacon && navigator.sendBeacon && !t) { navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: "application/json" })); return; }
    fetch(url, { method: "POST", keepalive: true, headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) }, body: JSON.stringify(body) }).catch(() => {});
  } catch { /* ignore */ }
};

// Route-driven events (resume tools, pricing).
const ROUTE_EVENTS = [[/^\/upgrade/, "pricing_view"], [/^\/(resume-studio|ai-studio)\/?([^/]*)/, "resume_tool_open"]];
let lastPv = ["", 0];
export const pageView = () => {
  const p = window.location.pathname;
  if (lastPv[0] === p && Date.now() - lastPv[1] < 1000) return;
  lastPv = [p, Date.now()];
  track("page_view");
  ROUTE_EVENTS.forEach(([re, name]) => { const m = p.match(re); if (m) track(name, name === "resume_tool_open" ? { tool: m[2] || m[1] } : {}); });
};

// API calls that represent user actions.
const API_EVENTS = [
  ["post", /^\/billing\/checkout$/, (b) => { try { sessionStorage.setItem(PLAN, b?.product || ""); } catch { /* */ } return ["checkout_started", { plan: b?.product, interval: b?.interval || "month" }]; }],
  ["get", /^\/jobs\/(ats-)?search$/, () => ["job_search", {}]],
  ["post", /^\/people-finder\/search$/, () => ["people_finder_search", {}]],
  ["post", /^\/ai\/chat\/companion\/start$/, () => ["companion_session_start", {}]],
  ["post", /^\/recruiter\/bookings$/, () => ["coaching_booking_start", {}]],
  ["post", /^\/(public\/)?(resume-)?scan/, () => ["free_scan_submitted", {}]],
  ["post", /trial/, () => ["trial_started", {}]],
];
export const apiEvent = (config) => {
  const m = (config.method || "get").toLowerCase();
  const path = String(config.url || "").split("?")[0];
  let body; try { body = typeof config.data === "string" ? JSON.parse(config.data) : config.data; } catch { body = null; }
  API_EVENTS.forEach(([meth, re, f]) => { if (meth === m && re.test(path)) { const [n, pr] = f(body); track(n, pr); } });
  if (["put", "post", "patch"].includes(m) && /^\/profile$/.test(path) && window.location.pathname.startsWith("/onboarding")) track("profile_completed");
};
export const apiError = (err) => { const s = err.response?.status; if (s && s >= 400 && !String(err.config?.url).includes("/analytics/")) track("api_error", { status: s, route: String(err.config?.url || "").split("?")[0].replace(/\/[0-9a-f-]{8,}/gi, "/:id").slice(0, 80) }); };
export const purchased = () => { let plan = ""; try { plan = sessionStorage.getItem(PLAN) || ""; } catch { /* */ } track("purchase", { plan }); };

const label = (el) => (el.getAttribute("aria-label") || el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 60);
const onClick = (e) => {
  const el = e.target.closest?.("a,button");
  if (!el || el.closest("[data-no-track]")) return;
  const tid = el.getAttribute("data-testid") || "";
  if (el.closest("nav,aside,header")) track("nav_click", { label: label(el) });
  if (/google-login/.test(tid)) track("registration_started");
  if (/cta|trial|get-started|upgrade|signup|sign-up|buy|start/i.test(tid) || el.hasAttribute("data-track")) track("cta_click", { label: label(el), location: window.location.pathname });
  if (/yearly|monthly|interval/i.test(tid)) track("plan_toggle", { interval: /year/i.test(`${tid}${label(el)}`) ? "year" : "month" });
};

let started = false;
export const startAnalytics = async () => {
  if (started) return; started = true;
  visitorId(); captureFirst();
  document.addEventListener("click", onClick, true);
  window.addEventListener("error", (e) => track("js_error", { message: String(e.message || "").slice(0, 120), browser: navigator.userAgent.match(/(Chrome|Firefox|Safari|Edg)\/[\d]+/)?.[0] || "other" }));
  setInterval(() => flush(false), 3000);
  window.addEventListener("pagehide", () => flush(true));
  try {
    const { onLCP, onCLS, onINP, onTTFB } = await import("web-vitals");
    const v = {}; let sent = false;
    const send = () => { if (!sent && Object.keys(v).length) { sent = true; track("web_vitals", { ...v }); flush(true); } };
    onLCP((m) => { v.lcp = Math.round(m.value); }); onCLS((m) => { v.cls = Number(m.value.toFixed(3)); }); onINP((m) => { v.inp = Math.round(m.value); }); onTTFB((m) => { v.ttfb = Math.round(m.value); });
    document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && send());
  } catch { /* ignore */ }
};
